import { useEffect, useState } from 'react';

const WINE = { r: 107, g: 61, b: 74 };

function blendWithBrand(r, g, b, brandWeight = 0.5) {
  return {
    r: Math.round(r * (1 - brandWeight) + WINE.r * brandWeight),
    g: Math.round(g * (1 - brandWeight) + WINE.g * brandWeight),
    b: Math.round(b * (1 - brandWeight) + WINE.b * brandWeight),
  };
}

/** Average colour from slide (for dynamic hero atmosphere). */
export function sampleHeroSlideTint(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const size = 28;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let r = 0;
        let g = 0;
        let b = 0;
        let n = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 48) continue;
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          n += 1;
        }
        if (!n) {
          resolve(null);
          return;
        }
        resolve(blendWithBrand(r / n, g / n, b / n));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** CSS variables for hero wrap / band — updates when slide changes. */
export function useHeroSlideTint(src) {
  const [tint, setTint] = useState(null);

  useEffect(() => {
    if (!src) {
      setTint(null);
      return undefined;
    }
    let cancelled = false;
    const run = () => {
      sampleHeroSlideTint(src).then((avg) => {
        if (!cancelled) setTint(avg);
      });
    };
    let idleId;
    let timeoutId;
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(run, 300);
    }
    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [src]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    if (!tint) return undefined;
    const rgb = `${tint.r}, ${tint.g}, ${tint.b}`;
    root.style.setProperty('--hero-slide-tint', rgb);
    root.style.setProperty('--hero-slide-tint-rgb', `rgb(${rgb})`);
    root.style.setProperty('--hero-slide-tint-soft', `rgba(${rgb}, 0.38)`);
    root.style.setProperty('--hero-slide-tint-glow', `rgba(${rgb}, 0.24)`);
    root.style.setProperty(
      '--hero-slide-tint-deep',
      `rgba(${Math.max(tint.r - 28, 0)}, ${Math.max(tint.g - 20, 0)}, ${Math.max(tint.b - 18, 0)}, 0.55)`
    );
    return () => {
      root.style.removeProperty('--hero-slide-tint');
      root.style.removeProperty('--hero-slide-tint-rgb');
      root.style.removeProperty('--hero-slide-tint-soft');
      root.style.removeProperty('--hero-slide-tint-glow');
      root.style.removeProperty('--hero-slide-tint-deep');
    };
  }, [tint]);

  if (!tint) return {};
  const rgb = `${tint.r}, ${tint.g}, ${tint.b}`;
  return {
    '--hero-slide-tint': rgb,
    '--hero-slide-tint-rgb': `rgb(${rgb})`,
    '--hero-slide-tint-soft': `rgba(${rgb}, 0.38)`,
    '--hero-slide-tint-glow': `rgba(${rgb}, 0.24)`,
    '--hero-slide-tint-deep': `rgba(${Math.max(tint.r - 28, 0)}, ${Math.max(tint.g - 20, 0)}, ${Math.max(tint.b - 18, 0)}, 0.55)`,
  };
}
